import { useCallback, useEffect, type FunctionComponent } from 'react';

import { getMemberId } from '@trello/authentication';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { client } from '@trello/graphql';
import { useDialog } from '@trello/nachos/dialog';
import { useSharedStateSelector } from '@trello/shared-state';

import { SmartScheduleOnboardingDialog } from './SmartScheduleOnboardingDialog';
import {
  SMART_SCHEDULE_ONBOARDING_MESSAGE_ID,
  smartScheduleOnboardingSharedState,
} from './smartScheduleOnboardingSharedState';
import { TrelloInitializeMemberAiPreferenceDocument } from './TrelloInitializeMemberAiPreferenceMutation.generated';

export const SmartScheduleOnboardingManager: FunctionComponent = () => {
  const { dismissOneTimeMessage } = useOneTimeMessagesDismissed();
  const { dialogProps, hide, show } = useDialog();
  const { isVisible, pendingAction, entryPoint } = useSharedStateSelector(
    smartScheduleOnboardingSharedState,
    useCallback((state) => state, []),
  );

  useEffect(() => {
    if (!isVisible) {
      return;
    }
    show();

    const memberId = getMemberId({ returnAri: true });
    if (memberId) {
      client
        .mutate({
          mutation: TrelloInitializeMemberAiPreferenceDocument,
          variables: { input: { userId: memberId } },
          context: { traceId: null },
        })
        .catch(() => {});
    }
  }, [isVisible, show]);

  const handleClose = useCallback(() => {
    hide();

    smartScheduleOnboardingSharedState.setValue({
      isVisible: false,
      pendingAction: null,
      entryPoint: null,
    });
  }, [hide]);

  const handleContinue = useCallback(async () => {
    await dismissOneTimeMessage(SMART_SCHEDULE_ONBOARDING_MESSAGE_ID, {
      optimistic: true,
    });
    hide();

    smartScheduleOnboardingSharedState.setValue({
      isVisible: false,
      pendingAction: null,
      entryPoint: null,
    });

    await pendingAction?.();
  }, [dismissOneTimeMessage, hide, pendingAction]);

  if (!isVisible) {
    return null;
  }

  return (
    <SmartScheduleOnboardingDialog
      dialogProps={dialogProps}
      onClose={handleClose}
      onContinue={handleContinue}
      entryPoint={entryPoint}
    />
  );
};
